import { useCallback, useEffect, useRef } from "react";
import { getFirehoseNewsFeed, isSourceAccessible } from ".";
import { NewsFeedItem } from "./models";
import { FETCH_STATUS } from "..";
import useFirehoseStore from "@/stores/FirehoseStore";
import { shallow } from "zustand/shallow";
import useAuth from "@/hooks/useAuth";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import { useGetAppRoles } from "@/hooks/useGetAppRoles";
import { useUserPreferences } from "@/pages/settings/hooks/useUserPreferences";
import { useTruthSocialNotificationSettings } from "@/pages/settings/hooks/useTruthSocialNotificationSettings";
import { useNotificationSoundSettings } from "@/pages/settings/hooks/useNotificationSoundSettings";
import { useBrowserNotificationSettings } from "@/pages/settings/hooks/useBrowserNotificationSettings";
import {
  usePortfoliosWithSoundEnabled,
  usePortfoliosWithPopupEnabled,
} from "./usePortfoliosWithSoundEnabled";
import firehoseSSEClient from "./firehoseSSEClient";
import {
  ingestFirehoseStory,
  normalizeSourceTime,
  setFirehoseAuthContext,
  setFirehoseSoundContext,
  setFirehoseBrowserNotifyContext,
} from "./ingestFirehoseStory";

/**
 * Custom React hook to fetch news feed data and subscribe to a Server-Sent Events (SSE) stream.
 * This hook is intended to be executed once on app load.
 * This hook itself does not return any data,
 * but rather manages the state of the news feed data
 * in the local store (FirehoseStore).
 *
 * @returns {Object} An object containing loading and error status flags.
 */
const useInitFirehoseStore = (): object => {
  const { accessToken, authenticated } = useAuth();

  const isInternalUser = useHasInternalNewsquawkUserRole();
  const roles = useGetAppRoles();

  // Ref so the SSE startup effect can read the current token without
  // tearing down and restarting the connection on every token refresh.
  // Token refreshes are propagated via firehoseSSEClient.updateUrlParams below.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  // Accessing the local store to manage stories
  const firehoseStore = useFirehoseStore(
    (state) => ({
      addStories: state.addAllStories,
      clearAllStories: state.clearAllStories,
      status: state.status,
      setStatus: state.setStatus,
    }),
    shallow
  );

  // Derived state to indicate if data is fetched successfully
  const isFetched = firehoseStore.status == FETCH_STATUS.SUCCESS;

  // Derived state to indicate if data is currently being loaded
  const isLoading = firehoseStore.status == FETCH_STATUS.LOADING;

  // Derived state to indicate if there was an error during data fetching
  const isError = firehoseStore.status == FETCH_STATUS.ERROR;

  const { notificationsSound, isLoading: isUserPrefLoading } =
    useUserPreferences();

  const { settings: truthSocialNotify } = useTruthSocialNotificationSettings();

  const { settings: notificationSoundSettings } =
    useNotificationSoundSettings();

  // Keep a stable reference to portfolios with sound enabled (based on alert mode setting)
  const portfoliosWithSoundEnabledRef = usePortfoliosWithSoundEnabled();

  // Browser (OS) pop-up notifications: global toggles + popup-enabled watchlists.
  const { settings: browserNotifySettings } = useBrowserNotificationSettings();
  const portfoliosWithPopupEnabledRef = usePortfoliosWithPopupEnabled();

  // Push current auth context into the shared ingest module so the polling
  // fallback (running outside of React) can apply the same access-control filter.
  useEffect(() => {
    setFirehoseAuthContext({ roles, isInternalUser });
  }, [roles, isInternalUser]);

  // Same for sound context. portfoliosWithSoundEnabledRef is a stable React
  // ref whose `.current` mutates without triggering effects, so we re-sync on
  // every render rather than via dep-tracked deps.
  useEffect(() => {
    setFirehoseSoundContext({
      portfoliosWithSoundEnabled: portfoliosWithSoundEnabledRef.current ?? null,
      notificationsSoundTruthSocial: notificationsSound.truth_social,
      truthSocialNotify,
      notificationSounds: {
        truthSocial: notificationSoundSettings.truthSocial,
        portfolio: notificationSoundSettings.portfolio,
      },
    });
  });

  // Same pattern for the browser pop-up context. Re-synced every render so the
  // outside-React ingest path reads current toggles + popup-enabled watchlists.
  useEffect(() => {
    setFirehoseBrowserNotifyContext({
      enabled: browserNotifySettings.enabled,
      truthSocial: browserNotifySettings.truthSocial,
      portfoliosWithPopupEnabled: portfoliosWithPopupEnabledRef.current ?? null,
    });
  });

  /**
   * Fetches the news feed data from the REST endpoint.
   * Updates the local store with the fetched stories.
   */
  const fetchNewsFeed = useCallback(async () => {
    firehoseStore.setStatus(FETCH_STATUS.LOADING);

    try {
      const response = (await getFirehoseNewsFeed()) as NewsFeedItem[];
      firehoseStore.setStatus(FETCH_STATUS.SUCCESS);

      // sort stories by id in descending order
      const responseSorted = response.sort((a, b) => b.id - a.id);

      const filteredResponse = responseSorted.filter((story) =>
        isSourceAccessible(story.backend_source, roles, isInternalUser)
      );
      filteredResponse.forEach(normalizeSourceTime);
      firehoseStore.addStories(filteredResponse);
    } catch (error) {
      firehoseStore.setStatus(FETCH_STATUS.ERROR);
      console.log(error);
    }
  }, [firehoseStore, isInternalUser, roles]);

  // useEffect to fetch news feed data
  useEffect(() => {
    if (!authenticated) return;

    if (!isFetched && !isLoading) fetchNewsFeed();
  }, [authenticated, isFetched, isLoading, fetchNewsFeed]);

  // Keep stored SSE params up to date when the token refreshes,
  // so that error-triggered reconnects use the latest token — without
  // tearing down the existing connection.
  useEffect(() => {
    if (accessToken) {
      firehoseSSEClient.updateUrlParams({ token: accessToken });
    }
  }, [accessToken]);

  useEffect(() => {
    if (!authenticated || !accessTokenRef.current) return;

    if (isUserPrefLoading) return;

    firehoseSSEClient.start({
      urlParams: {
        token: accessTokenRef.current,
      },
    });

    const unsubStatusChange = firehoseSSEClient.subscribe(
      "onStatusChange",
      () => {
        // No-op for now; status transitions are handled internally by the client.
      }
    );

    const unsubMessage = firehoseSSEClient.subscribe(
      "message",
      (newStory: NewsFeedItem) => {
        ingestFirehoseStory(newStory);
      }
    );

    return () => {
      unsubStatusChange();
      unsubMessage();
    };
  }, [authenticated, isUserPrefLoading]);

  return {
    isLoading,
    isError,
  };
};

export default useInitFirehoseStore;
